import { MaterialCheckinPassQR } from "../../../common/components/MaterialCheckinPassQR";
import { useCheckinPass } from "../hooks/useCheckinPass";
import { CHECKIN_PASS_REFUSAL, checkinPassRefusal } from "../../../common/checkinPassRefusal";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * The drop-in check-in pass card, shown on the session hub while the player is
 * `pending_validation` under a facility's staff_check_in mode. "Show this to
 * staff to start playing" — staff scan the QR to validate the paid player onto
 * the floor, which starts the drop-in timer server-side.
 *
 * A thin clone of BookingPassDialog's body: it mints the pass via
 * useCheckinPass(facilityId) and renders the SHARED CheckinPassQR (the scannable
 * QR + mm:ss countdown + single-use label + refresh). All server truth — the
 * token + expiry come from the backend; this card computes nothing. Honest
 * states: an in-flight pass shows a "Generating…" status (no fabricated code);
 * a 404/403/409 rejection surfaces an honest error with NO QR and a refresh that
 * re-fetches the pass. The signed token is QR-only; raw resource identifiers and
 * credentials are never rendered as text.
 */
export function CheckinPassCard({
  facilityId,
  label = "Drop-in check-in",
}: {
  facilityId: string;
  label?: string;
}) {
  const passQ = useCheckinPass(facilityId, true);
  // Checked in on this session, or it ended: no new QR (#2691).
  const refusal = passQ.isError ? checkinPassRefusal(passQ.error) : null;
  if (refusal) {
    return (
      <Alert severity="info" role="status">
        <strong>{CHECKIN_PASS_REFUSAL[refusal].label}.</strong>{" "}
        {CHECKIN_PASS_REFUSAL[refusal].message}
      </Alert>
    );
  }

  if (passQ.isError) {
    return (
      <Alert severity="error" role="alert">
        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
          <span>We couldn&apos;t get your check-in pass.</span>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => void passQ.refetch()}
          >
            Get a new pass
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (passQ.data) {
    return (
      <MaterialCheckinPassQR
        token={passQ.data.token}
        expiresAt={passQ.data.expires_at}
        label={label}
        onRefresh={() => void passQ.refetch()}
      />
    );
  }

  return (
    <Typography color="text.secondary" role="status">
      Generating your check-in pass…
    </Typography>
  );
}
